import { isIos, isStandalone } from '@/shared/pwa/install';

import { subscribePush, unsubscribePush, type PushConfig, type PushDevice, type PushSubscriptionRequest } from './api';

/*
 * Web push on this device: asking for the permission, subscribing the browser to the portal's push messages (the Push API,
 * through the service worker), and telling the server about the subscription. The server keeps one registration per browser
 * subscription; this file keeps one flag — "push is on in this browser" — so that every start of the portal registers the
 * subscription again (idempotent) and a revoked permission switches the device off.
 */

const FLAG_KEY = 'orbita.notifications.push';
/** The server's id of this browser's registration (to mark "this device" in the user's list). */
const DEVICE_KEY = 'orbita.notifications.push.device';

export type PushSupport =
  /** This browser can receive push messages. */
  | 'supported'
  /** iPhone/iPad in a browser tab: push works only in the app added to the home screen. */
  | 'needs-install'
  /** No Push API (an old browser, an insecure address, a private window of some browsers). */
  | 'unsupported';

export function pushSupport(): PushSupport {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return 'unsupported';
  if ('serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window) return 'supported';
  return isIos() && !isStandalone() ? 'needs-install' : 'unsupported';
}

export type PushPermission = NotificationPermission | 'unsupported';

export function pushPermission(): PushPermission {
  return typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported';
}

export type PushFailure =
  /** The user (or the browser's settings) refused the permission. */
  | 'denied'
  /** The permission prompt was closed without an answer. */
  | 'dismissed'
  | 'unsupported'
  /** The portal has no VAPID keys. */
  | 'not-configured'
  /** The service worker is not running (not registered yet, or the browser blocked it). */
  | 'no-worker'
  /** The browser's push service refused the subscription (offline, blocked by the network, a private window). */
  | 'subscribe-failed';

/** Why push could not be turned on; `reason` tells the user what to do. */
export class PushError extends Error {
  readonly reason: PushFailure;

  constructor(reason: PushFailure, message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'PushError';
    this.reason = reason;
  }
}

/* ---------- the "on in this browser" flag ---------- */

export function isDevicePushFlagged(): boolean {
  try {
    return localStorage.getItem(FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

function setFlag(on: boolean, deviceId?: string): void {
  try {
    if (on) localStorage.setItem(FLAG_KEY, '1');
    else {
      localStorage.removeItem(FLAG_KEY);
      localStorage.removeItem(DEVICE_KEY);
    }
    if (on && deviceId) localStorage.setItem(DEVICE_KEY, deviceId);
  } catch {
    // Storage unavailable: the choice lasts until the page closes (the subscription itself lives in the browser).
  }
}

/** The id the server gave this browser's registration, if push is on here. */
export function thisDeviceId(): string | null {
  try {
    return localStorage.getItem(DEVICE_KEY);
  } catch {
    return null;
  }
}

/* ---------- browser pieces ---------- */

/** `applicationServerKey` as bytes (the form every browser accepts). */
export function decodeKey(base64Url: string): Uint8Array<ArrayBuffer> {
  const padded = base64Url.replace(/-/gu, '+').replace(/_/gu, '/').padEnd(Math.ceil(base64Url.length / 4) * 4, '=');
  const binary = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function sameBytes(a: ArrayBuffer | null, b: Uint8Array): boolean {
  return a?.byteLength === b.byteLength && new Uint8Array(a).every((value, index) => value === b[index]);
}

/** The registration of the portal's worker, or null when there is none (it never resolves without a registered worker: bounded). */
export async function workerRegistration(timeoutMs = 8000): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), timeoutMs);
  });
  try {
    return await Promise.race([navigator.serviceWorker.ready, timeout]);
  } finally {
    clearTimeout(timer);
  }
}

/** The browser's subscription for this portal, if it has one. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  const registration = await workerRegistration(2000);
  return registration ? registration.pushManager.getSubscription() : null;
}

/**
 * The browser's subscription to the portal's key: the existing one when it belongs to this key, a new one otherwise (a
 * subscription made for another key — the portal's keys were replaced — is useless: the push service would refuse every
 * message of this server).
 */
export async function ensureSubscription(
  registration: Pick<ServiceWorkerRegistration, 'pushManager'>,
  publicKey: string,
): Promise<PushSubscription> {
  const key = decodeKey(publicKey);
  const existing = await registration.pushManager.getSubscription();
  if (existing) {
    if (sameBytes(existing.options.applicationServerKey, key)) return existing;
    await existing.unsubscribe();
  }
  try {
    return await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  } catch (error) {
    throw new PushError('subscribe-failed', 'The browser could not subscribe to push messages.', { cause: error });
  }
}

/** A short name of this browser and system for the user's device list ("Chrome · macOS"). */
export function deviceLabel(userAgent: string = navigator.userAgent): string {
  const has = (...needles: string[]) => needles.some((needle) => userAgent.includes(needle));
  const browser = has('Edg/')
    ? 'Edge'
    : has('OPR/')
      ? 'Opera'
      : has('Firefox/')
        ? 'Firefox'
        : has('Chrome/', 'CriOS/')
          ? 'Chrome'
          : has('Safari/')
            ? 'Safari'
            : 'Browser';
  const system = has('iPhone', 'iPad', 'iPod')
    ? 'iOS'
    : has('Android')
      ? 'Android'
      : has('Windows')
        ? 'Windows'
        : has('Mac OS X', 'Macintosh')
          ? 'macOS'
          : has('CrOS')
            ? 'ChromeOS'
            : has('Linux')
              ? 'Linux'
              : '';
  return system ? `${browser} · ${system}` : browser;
}

/** The registration request for a browser subscription (`PushSubscription.toJSON()` plus the device's name). */
export function toRequest(subscription: PushSubscription, label: string | null): PushSubscriptionRequest {
  const json = subscription.toJSON();
  const { p256dh, auth } = json.keys ?? {};
  if (!json.endpoint || !p256dh || !auth) {
    throw new PushError('subscribe-failed', 'The browser returned an incomplete push subscription.');
  }
  return { endpoint: json.endpoint, expirationTime: json.expirationTime ?? null, keys: { p256dh, auth }, label };
}

/* ---------- on, off, and the start-up check ---------- */

/**
 * Turns push on in this browser (call it from a click: the permission prompt needs a user gesture): asks for the permission,
 * subscribes, registers the subscription with the server and remembers that push is on here.
 * @throws {PushError} with a reason the UI explains.
 */
export async function enableDevicePush(config: PushConfig | undefined): Promise<PushDevice> {
  if (pushSupport() !== 'supported') throw new PushError('unsupported', 'This browser cannot receive push messages.');
  if (!config?.enabled || !config.publicKey) throw new PushError('not-configured', 'Web push is not set up on this portal.');
  let permission: PushPermission = pushPermission();
  if (permission === 'default') permission = await Notification.requestPermission();
  if (permission === 'denied') throw new PushError('denied', 'Notifications are blocked for this site.');
  if (permission !== 'granted') throw new PushError('dismissed', 'The permission prompt was closed.');
  const registration = await workerRegistration();
  if (!registration) throw new PushError('no-worker', 'The service worker is not running.');
  const subscription = await ensureSubscription(registration, config.publicKey);
  const device = await subscribePush(toRequest(subscription, deviceLabel()));
  setFlag(true, device.id);
  return device;
}

/** Turns push off in this browser: the server forgets the device first, then the browser drops the subscription. */
export async function disableDevicePush(): Promise<void> {
  const subscription = await currentSubscription();
  setFlag(false);
  if (!subscription) return;
  try {
    await unsubscribePush(subscription.endpoint);
  } finally {
    await subscription.unsubscribe().catch(() => false);
  }
}

/**
 * At every start of the signed-in portal: when push is on in this browser, register the subscription again (it follows a
 * signed-in account, refreshes the keys, recreates it if the browser lost it); when the permission was withdrawn meanwhile,
 * switch the device off. Never asks the user for anything.
 * @returns whether push is on in this browser afterwards.
 */
export async function syncDevicePush(config: PushConfig | undefined): Promise<boolean> {
  if (!isDevicePushFlagged() || pushSupport() !== 'supported' || !config?.enabled || !config.publicKey) return false;
  if (pushPermission() !== 'granted') {
    setFlag(false);
    return false;
  }
  const registration = await workerRegistration();
  if (!registration) return false;
  const subscription = await ensureSubscription(registration, config.publicKey);
  const device = await subscribePush(toRequest(subscription, deviceLabel()));
  setFlag(true, device.id);
  return true;
}

/**
 * Signing out: this device must not keep receiving the signed-out user's notifications (a shared computer). The server drops
 * the registration of this browser's subscription; the browser keeps the subscription for the next person who turns push on.
 */
export async function forgetDevicePush(): Promise<void> {
  if (!isDevicePushFlagged() || pushSupport() !== 'supported') return;
  const subscription = await currentSubscription();
  setFlag(false);
  if (subscription) await unsubscribePush(subscription.endpoint);
}
